import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../../context/GameContext';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft } = FiIcons;

const EPISODES = [
  {
    id: 1,
    title: "The Crayon Crisis",
    scene: "A classmate drops their crayons all over the floor. Bunny sees this happen.",
    choices: [
      { text: "Help pick them up", emoji: "🤝", isRight: true, outcome: "The classmate smiles! 'Thanks Bunny!' They become friends." },
      { text: "Walk away laughing", emoji: "😂", isRight: false, outcome: "The classmate feels sad. The teacher looks disappointed." }
    ]
  },
  {
    id: 2,
    title: "The Angry Moment",
    scene: "Bunny is angry because someone took their turn. Bunny's face feels hot.",
    choices: [
      { text: "Yell and hit", emoji: "😠", isRight: false, outcome: "Everyone gets scared. Bunny has to sit out of the game." },
      { text: "Take a deep breath and say 'I'm upset'", emoji: "🌬️", isRight: true, outcome: "The teacher helps solve the problem. Everyone respects Bunny's words." }
    ]
  },
  {
    id: 3,
    title: "The Lonely Lunch",
    scene: "Bunny sees someone sitting alone at lunch, looking at their sandwich.",
    choices: [
      { text: "Invite them to sit together", emoji: "💺", isRight: true, outcome: "They have a great time! 'Thanks for including me!' the kid says." },
      { text: "Ignore them", emoji: "🙈", isRight: false, outcome: "The kid eats alone. Bunny wonders if they should have been kinder." }
    ]
  },
  {
    id: 4,
    title: "The Broken Toy",
    scene: "Bunny accidentally breaks a friend's toy while playing.",
    choices: [
      { text: "Hide it and say nothing", emoji: "🙊", isRight: false, outcome: "The friend finds out later and feels betrayed. Trust is broken." },
      { text: "Say sorry and tell a grown-up", emoji: "🙏", isRight: true, outcome: "'I'm sorry,' Bunny says. The friend appreciates the honesty. They fix it together." }
    ]
  },
  {
    id: 5,
    title: "The Winner's Grace",
    scene: "Bunny wins the race! Everyone is watching.",
    choices: [
      { text: "Brag and say 'I'm the best!'", emoji: "🏆", isRight: false, outcome: "Other kids feel bad. No one wants to play with Bunny next time." },
      { text: "Cheer for everyone who tried", emoji: "👏", isRight: true, outcome: "'Great effort everyone!' Bunny says. The class celebrates together!" }
    ]
  }
];

export default function SELBunnyChoices() {
  const navigate = useNavigate();
  const { dispatch } = useGame();
  const [currentEpisode, setCurrentEpisode] = useState(0);
  const [selectedPath, setSelectedPath] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [stars, setStars] = useState(0);
  const [completed, setCompleted] = useState(false);

  const episode = EPISODES[currentEpisode];

  const makeChoice = (choice, index) => {
    setSelectedPath(index);
    setShowOutcome(true);
    if (choice.isRight) {
      setStars(s => s + 1);
      dispatch({ type: 'ADD_SCORE', payload: 15 });
    }
  };

  const nextEpisode = () => {
    if (currentEpisode < EPISODES.length - 1) {
      setCurrentEpisode(c => c + 1);
      setSelectedPath(null);
      setShowOutcome(false);
    } else {
      setCompleted(true);
      dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'sel-bunny-choices', bonusTokens: stars * 5 + 15 } });
    }
  };

  if (completed) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-purple-100 to-pink-100">
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-purple-200 text-center max-w-md">
          <motion.div animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="text-6xl mb-4">🐰</motion.div>
          <h2 className="text-3xl font-kid text-purple-700 mb-4">Bunny's Best Friend!</h2>
          <div className="bg-purple-50 rounded-2xl p-4 mb-6">
            <p className="font-comic text-purple-600">You made {stars} kind choices! ⭐</p>
            <p className="font-comic text-sm text-purple-500 mt-2">Bunny is lucky to have a friend like you!</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="w-full bg-purple-500 text-white p-3 rounded-2xl font-kid">Back to Dashboard</button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen p-4 pb-24 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 bg-white rounded-2xl px-4 py-2 shadow border-2 border-purple-200">
            <SafeIcon icon={FiArrowLeft} /> Back
          </button>
          <div className="bg-purple-100 rounded-2xl px-4 py-2 shadow border-2 border-purple-300">
            <span className="font-kid text-purple-700">Episode {currentEpisode + 1}</span>
          </div>
          <div className="bg-yellow-100 rounded-2xl px-4 py-2 shadow border-2 border-yellow-300">
            <span className="font-kid text-yellow-700">⭐ {stars}</span>
          </div>
        </div>

        {/* Episode Card */}
        <AnimatePresence mode="wait">
          <motion.div 
            key={episode.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-white rounded-3xl p-8 shadow-xl border-4 border-purple-200"
          >
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">📺</div>
              <h2 className="font-kid text-2xl text-purple-700 mb-2">{episode.title}</h2>
            </div>

            <div className="bg-purple-50 rounded-2xl p-6 mb-8 border-2 border-purple-100">
              <p className="font-comic text-lg text-gray-700 text-center">{episode.scene}</p>
            </div>

            {!showOutcome ? (
              <div className="grid md:grid-cols-2 gap-4">
                {episode.choices.map((choice, idx) => (
                  <motion.button
                    key={idx}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => makeChoice(choice, idx)}
                    className="bg-white border-4 border-purple-200 rounded-2xl p-6 shadow-lg hover:border-purple-400 transition-all text-center"
                  >
                    <div className="text-4xl mb-3">{choice.emoji}</div>
                    <span className="font-kid text-purple-700">{choice.text}</span>
                  </motion.button>
                ))}
              </div>
            ) : (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl p-6 text-center border-4 ${episode.choices[selectedPath].isRight ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}
              >
                <div className="text-5xl mb-4">
                  {episode.choices[selectedPath].isRight ? '🌟' : '📖'}
                </div>
                <h3 className="font-kid text-xl mb-3">
                  {episode.choices[selectedPath].isRight ? 'Great Choice!' : 'Learning Moment'}
                </h3>
                <p className="font-comic text-gray-700 mb-6">{episode.choices[selectedPath].outcome}</p>
                
                <div className="bg-white rounded-xl p-4 mb-4 border-2 border-purple-100">
                  <p className="font-comic text-sm text-purple-600">
                    💡 <strong>Kindness Tip:</strong> {episode.choices[selectedPath].isRight 
                      ? "This choice showed empathy and kindness!"
                      : "Next time, think about how others might feel."
                    }
                  </p>
                </div>

                <button 
                  onClick={nextEpisode}
                  className="bg-purple-500 text-white px-6 py-3 rounded-2xl font-kid hover:bg-purple-600 transition-colors"
                >
                  {currentEpisode < EPISODES.length - 1 ? 'Next Episode →' : 'See Results! 🎉'}
                </button>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}